import { driveFilesList, driveCreateMetadataOnly, type DriveFile } from './driveClient';

/**
 * ============================================================================
 * GOOGLE DRIVE — DEDICATED BACKUP FOLDER (Stage 3b)
 * ============================================================================
 *
 * Finds the app's one dedicated backup folder if it already exists, or
 * creates it if it doesn't — never both, never a duplicate. Per the
 * approved plan, folder identity is established by FOUR conditions
 * together, not name alone:
 *   1. mimeType is a Drive folder
 *   2. name matches exactly
 *   3. not trashed
 *   4. carries this app's appProperties marker
 *
 * Because this app only ever requests the `drive.file` OAuth scope, the
 * Drive API itself already guarantees that files.list() can only ever
 * return files/folders THIS APP created — a folder created by another
 * app, or manually by the user with the same name, is invisible to this
 * query regardless of how it's phrased (see the Stage 3 architecture
 * assessment). The appProperties marker is the additional, explicit
 * confirmation on top of that scope-level isolation — not the only
 * layer of protection against picking the wrong folder.
 */

export const BACKUP_FOLDER_NAME = 'LIC HFL Loan Tracker Backups';
export const APP_PROPERTY_KEY = 'app';
export const APP_PROPERTY_VALUE = 'lic-hfl-loan-tracker';

const FOLDER_MIME_TYPE = 'application/vnd.google-apps.folder';

/**
 * Pure decision logic, deliberately separated from the network call
 * that produces `candidates` — this is what makes the decoy-rejection
 * and folder-selection behaviour directly unit-testable (see
 * driveFolder.test.ts) without mocking fetch() or hitting a real Drive
 * account. Selects the first candidate that satisfies every one of the
 * four conditions above; returns undefined if none qualify (the caller
 * then creates a new folder).
 */
export function selectBackupFolder(candidates: DriveFile[]): DriveFile | undefined {
  return candidates.find(
    (file) =>
      file.mimeType === FOLDER_MIME_TYPE &&
      file.name === BACKUP_FOLDER_NAME &&
      file.appProperties?.[APP_PROPERTY_KEY] === APP_PROPERTY_VALUE
  );
}

function folderSearchQuery(): string {
  // trashed=false is enforced by Drive's default search behaviour being
  // overridden per-query, so it is stated explicitly here rather than
  // relied upon implicitly.
  return [
    `mimeType = '${FOLDER_MIME_TYPE}'`,
    `name = '${BACKUP_FOLDER_NAME}'`,
    'trashed = false',
    `appProperties has { key='${APP_PROPERTY_KEY}' and value='${APP_PROPERTY_VALUE}' }`,
  ].join(' and ');
}

/**
 * Searches for the dedicated backup folder (does NOT create one if
 * missing — see findOrCreateBackupFolder below for the create path).
 * Exported so callers that only need to know "does the folder already
 * exist" (e.g. listing backups) can reuse this exact search rather than
 * re-writing the query elsewhere.
 */
export async function findExistingBackupFolder(accessToken: string): Promise<DriveFile | undefined> {
  const candidates = await driveFilesList(
    accessToken,
    folderSearchQuery(),
    'files(id,name,mimeType,trashed,appProperties)'
  );
  return selectBackupFolder(candidates);
}

/**
 * Finds the dedicated backup folder if it exists, or creates it if it
 * doesn't. Never creates a duplicate: the search above already filters
 * to exactly the folders that would count as "the intended folder", so
 * any result found here is reused rather than triggering a fresh
 * create.
 */
export async function findOrCreateBackupFolder(accessToken: string): Promise<string> {
  const existing = await findExistingBackupFolder(accessToken);
  if (existing) {
    return existing.id;
  }

  const created = await driveCreateMetadataOnly(accessToken, {
    name: BACKUP_FOLDER_NAME,
    mimeType: FOLDER_MIME_TYPE,
    appProperties: { [APP_PROPERTY_KEY]: APP_PROPERTY_VALUE },
  });
  return created.id;
}
